import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@alertwhale/ui'
import { Activity, Send, AlertCircle } from 'lucide-react'

export function TestComponentsPage() {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectValue, setSelectValue] = useState('')
  const [priorityValue, setPriorityValue] = useState('default')
  const [inputValue, setInputValue] = useState('')

  return (
    <div className="min-h-screen bg-slate-950 p-8">
      <div className="mx-auto max-w-2xl space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Component Test Page</h1>
          <p className="text-slate-400">Testing redesigned modals, forms, and dropdowns</p>
        </div>

        {/* Form Inputs Test */}
        <div className="space-y-4 rounded-2xl border border-slate-800 bg-slate-900 p-6">
          <h2 className="text-xl font-semibold text-white">Form Inputs</h2>

          <div className="space-y-2">
            <Label htmlFor="test-input">Test Input</Label>
            <Input
              id="test-input"
              placeholder="Type something..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="test-select">Test Select (Session Type)</Label>
            <Select value={selectValue} onValueChange={setSelectValue}>
              <SelectTrigger id="test-select">
                <SelectValue placeholder="Select an option" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="delivery">Delivery</SelectItem>
                <SelectItem value="ride">Ride</SelectItem>
                <SelectItem value="order">Order</SelectItem>
                <SelectItem value="flight">Flight</SelectItem>
                <SelectItem value="workout">Workout</SelectItem>
                <SelectItem value="timer">Timer</SelectItem>
                <SelectItem value="progress">Progress</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="priority-select">Priority Select</Label>
            <Select value={priorityValue} onValueChange={setPriorityValue}>
              <SelectTrigger id="priority-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Default</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Buttons Test */}
        <div className="space-y-4 rounded-2xl border border-slate-800 bg-slate-900 p-6">
          <h2 className="text-xl font-semibold text-white">Buttons</h2>
          <div className="flex flex-wrap gap-3">
            <Button>Default</Button>
            <Button variant="outline">Outline</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="destructive">Destructive</Button>
            <Button variant="success">Success</Button>
            <Button variant="glow">Glow</Button>
          </div>
        </div>

        {/* Modal Trigger */}
        <div className="space-y-4 rounded-2xl border border-slate-800 bg-slate-900 p-6">
          <h2 className="text-xl font-semibold text-white">Modal Dialog</h2>
          <Button onClick={() => setDialogOpen(true)}>
            <Activity className="h-4 w-4" />
            Open Test Modal
          </Button>
        </div>

        {/* Test Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-[520px]">
            <DialogHeader>
              <DialogTitle>Test Modal Dialog</DialogTitle>
              <DialogDescription>
                This is a test modal with form inputs and dropdowns
              </DialogDescription>
            </DialogHeader>

            <form className="space-y-5 pt-2">
              <div className="flex items-center gap-3 rounded-xl bg-cyan-500/10 border border-cyan-500/20 p-4 text-sm text-cyan-400">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>This is an info message inside the modal</span>
              </div>

              <div className="space-y-2">
                <Label htmlFor="modal-input">
                  Title <span className="text-red-400">*</span>
                </Label>
                <Input
                  id="modal-input"
                  placeholder="Enter a title..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="modal-type">Session Type</Label>
                  <Select defaultValue="delivery">
                    <SelectTrigger id="modal-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="delivery">Delivery</SelectItem>
                      <SelectItem value="ride">Ride</SelectItem>
                      <SelectItem value="order">Order</SelectItem>
                      <SelectItem value="flight">Flight</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="modal-priority">Priority</Label>
                  <Select defaultValue="default">
                    <SelectTrigger id="modal-priority">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">Default</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="modal-body">Description</Label>
                <Input
                  id="modal-body"
                  placeholder="Optional description..."
                />
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="button" onClick={() => setDialogOpen(false)}>
                  <Send className="h-4 w-4" />
                  Submit
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  )
}
